import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  effect,
  EventEmitter,
  input,
  Input,
  OnInit,
  Output,
  signal
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { SvgIconComponent } from '@k12/ui-primitives';
import { PromptComponent, PromptInput, PromptInputOption } from '@k12/models';
import { MatError } from '@angular/material/input';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatButtonModule, MatCheckboxModule, CommonModule, FormsModule, SvgIconComponent, MatError],

  selector: 'lib-pc-select-sub-select',
  styleUrl: './pc-select-sub-select.component.scss',
  templateUrl: './pc-select-sub-select.component.html'
})
export class PcSelectSubSelectComponent implements OnInit {
  public allowPointerEvents = input<boolean>(false);
  public configJson = input<PromptComponent>(<PromptComponent>{});
  public inputNavigationInvalid = input<boolean[]>([]);

  @Input() set showEditButton(value: boolean | undefined) {
    this.isEditButton.set(value);
  }

  @Output() buttonSelect = new EventEmitter();
  @Output() cbConfig = new EventEmitter();
  @Output() checkboxSelect = new EventEmitter();
  @Output() deleteButtonSelect = new EventEmitter();
  @Output() editButtonSelect = new EventEmitter();

  public isEditButton = signal<boolean | undefined>(undefined);
  public showSubSelectTitle = signal<boolean>(false);

  constructor() {
    effect(() => {
      if (this.configJson()) {
        this.setShowSubSelectTitle();
      }
    });
  }

  public ngOnInit() {
    this.cbConfig.emit(this.configJson);
  }

  public checkboxClick(value: PromptInput): void {
    this.setShowSubSelectTitle(value);
    const parentId = this.configJson().id;
    this.configJson().inputs.forEach((input) => {
      if (input !== value && input.options && input.options.length > 0) {
        input.options.forEach((option) => {
          option.selected = false;
        });
      }
    });

    if (!value?.selected) {
      this.clearSubChoices(value);
    }
    this.checkboxSelect.emit({ value, parentId });
  }

  public subSelectButtonClicked(sub: PromptInputOption, subSelectInputId: string, parentId: string): void {
    const selectedInput = this.configJson().inputs.find((promptInput) => promptInput.id === subSelectInputId);
    // Therapy is single-select; other parent inputs allow multi-select sub-options.
    if (selectedInput?.value === 'therapy') {
      selectedInput.options?.forEach((option) => {
        option.selected = option.value === sub.value;
      });
    } else {
      sub.selected = !sub.selected;
    }
    this.buttonSelect.emit({ sub, subSelectInputId, parentId });
  }

  public clearSubChoices(value: PromptInput): void {
    value.options?.forEach((selectValue: PromptInputOption) => {
      selectValue.selected = false;
    });
  }

  public onEditSelect(editIndex: number): void {
    this.editButtonSelect.emit(editIndex);
  }

  public removeButton(optionIndex: number) {
    this.deleteButtonSelect.emit(optionIndex);
  }

  private setShowSubSelectTitle(selectedInput?: PromptInput): void {
    if (selectedInput) {
      this.configJson().inputs.forEach((input) => {
        input.selected = input === selectedInput ? selectedInput.selected : false;
      });
    }

    this.showSubSelectTitle.set(
      this.configJson().inputs.some((input) => input.selected && input.options && input.options.length > 0)
    );
  }
}
